import { Capacitor } from '@capacitor/core'
import { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { unparse } from 'papaparse'

import { ExportOptions } from '@types'

import { CSV_SEPARATOR } from '@/const/common'
import { ASSET_CURRENCY_TYPE } from '@/entities/assets/utils/const'

import { getCurrentDate } from './dates'

import faro from '@/services/faro'

export const getAppMode = (appMode: string) => {
  return Capacitor.isNativePlatform() ? `${appMode}-capacitor` : appMode
}

export const handleCatchedError = (errorMessage: string, options?: any) => {
  if (!faro) {
    console.log(errorMessage)
  } else {
    const args = [errorMessage]
    if (options) {
      args.push(JSON.stringify(options))
    }
    faro.log.error(args)
  }
}

export const handleCatchedRequestError = (
  req: InternalAxiosRequestConfig,
  resp: AxiosResponse<any, any>,
) => {
  const url = `${req.baseURL}/${req.url!}`
  if (!faro) {
    console.log(req, resp)
  } else {
    if (resp) {
      faro.log.error([`Request failed. url=${url} status=${resp.status}`], {
        url: url,
        params: JSON.stringify(req.params!),
        method: req.method!,
        response_status: String(resp.status),
        data: JSON.stringify(req.data),
        response_data: JSON.stringify(resp.data),
        correlation_id: req.headers['x-correlation-id'],
      })
    } else {
      faro.log.error([`Request failed without response. url=${url}`], {
        url: url,
        params: JSON.stringify(req.params!),
        method: req.method!,
        data: JSON.stringify(req.data),
        correlation_id: req.headers['x-correlation-id'],
      })
    }
  }
}

export const downloadAsFile = (
  content: BlobPart,
  name: string,
  ext = 'json',
): void => {
  const file = new Blob([content], { type: 'application/json' })
  const a = document.createElement('a')
  const url = URL.createObjectURL(file)
  a.href = url
  const fileName = `${name}_${getCurrentDate({ withTime: true })}`
  a.download = fileName.replace(/[^a-z0-9]/gi, '_').toLowerCase() + `.${ext}`
  document.body.appendChild(a)
  a.click()
  setTimeout(function () {
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }, 0)
}

export const getCurrencySymbol = (currencyName?: string): string => {
  try {
    const symbol =
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyName,
      })
        .formatToParts()
        .find(item => item.type === ASSET_CURRENCY_TYPE)?.value || ''
    return symbol === currencyName ? `${symbol} ` : symbol
  } catch {
    return ''
  }
}

export const waitForNode = (selector: string): Promise<Element> => {
  return new Promise(resolve => {
    const elm = document.querySelector(selector)
    if (elm) {
      return resolve(elm)
    }

    const observer = new MutationObserver(() => {
      const elm = document.querySelector(selector)
      if (elm) {
        resolve(elm)
        observer.disconnect()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
  })
}

export const appendScript = async (
  src: string,
  id?: string,
  attrs?: Record<string, string>,
  refresh = false,
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    if (id && document.getElementById(id)) {
      if (!refresh) {
        resolve(true)
        return
      }
      document.getElementById(id)?.remove()
    }
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = src
    id && (script.id = id)
    attrs &&
      Object.entries(attrs).map(([key, value]) => {
        script.setAttribute(key, value)
      })
    script.onload = resolve
    script.onerror = reject

    document.body.appendChild(script)
  })
}

export const injectCSS = async (
  css: string,
  id?: string,
  attrs?: Record<string, string>,
  refresh = false,
): Promise<unknown> => {
  return new Promise(resolve => {
    if (id && document.getElementById(id)) {
      if (!refresh) {
        resolve(true)
        return
      }
      document.getElementById(id)?.remove()
    }
    const style = document.createElement('style')
    style.innerText = css
    document.head.appendChild(style)
    id && (style.id = id)
    attrs &&
      Object.entries(attrs).map(([key, value]) => {
        style.setAttribute(key, value)
      })
    document.body.appendChild(style)
  })
}

export const handleExternalLink = (url: string) => {
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', url)
  link.setAttribute('target', '_blank')
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === '[object Object]'
}

export const trimRequestPayload = (value: unknown): unknown => {
  if (typeof value === 'string') return value.trim()

  if (Array.isArray(value)) {
    return value.map(item => trimRequestPayload(item))
  }

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    const result = new FormData()
    value.forEach((item, key) => {
      result.append(key, typeof item === 'string' ? item.trim() : item)
    })
    return result
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, trimRequestPayload(item)]),
    )
  }

  return value
}

export const prepareExportParams = (options: ExportOptions) => {
  const params: Record<string, string> = {}

  if (options.account_id) {
    params.account_id = options.account_id
  }

  // If transaction_ids is provided, return only that param
  if (options.transaction_ids) {
    params.transaction_ids = Array.isArray(options.transaction_ids)
      ? options.transaction_ids.join(',')
      : options.transaction_ids
    return params
  }

  if (options.search) {
    params.search = options.search
  }
  if (typeof options.filter !== 'string') {
    params.filter = JSON.stringify(options.filter)
  } else if (typeof options.filter === 'string') {
    params.filter = options.filter
  }
  if (typeof options.sort !== 'string') {
    params.sort = JSON.stringify(options.sort)
  } else if (typeof options.sort === 'string') {
    params.sort = options.sort
  }
  return params
}

export const sanitizeFileName = (name: string) => {
  return name.replaceAll(/[\,\/]/g, '').replaceAll(/[^a-z0-9\-]/gi, '_').toLowerCase()
}

const isSqlNumeric = (val: unknown): val is number | bigint =>
  (typeof val === 'number' && Number.isFinite(val)) || typeof val === 'bigint'

export const formatSqlValue = (val: unknown) => {
  if (isSqlNumeric(val)) {
    return String(val)
  }
  const str = String(val ?? '')
  return `'${escapeSqlString(str)}'`
}

export const escapeSqlString = (value: string | number) => {
  return String(value).replace(/'/g, '\'\'')
}

export const escapeSqlIdentifier = (value: string | number) => {
  return String(value).replace(/"/g, '""')
}

export const formatSqlIdentifier = (value: string): string => `"${escapeSqlIdentifier(value)}"`

export const saveToFile = (content: string, name: string, ext = 'json', type = 'application/json') => {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', `${sanitizeFileName(name)}.${ext}`)
  document.body.appendChild(link)
  link.click()
  setTimeout(function () {
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }, 0)
}

export const exportCSV = <T>(name: string, fields: string[], data: T[], header = true) => {
  const csv = unparse({ fields, data }, { delimiter: CSV_SEPARATOR, header })

  saveToFile(csv, `${name}${getCurrentDate({ withTime: true })}`, 'csv', 'text/csv;charset=UTF-8')
}
